'use client';

import { use } from 'react';

import {
  Avatar,
  CardActionArea,
  CardHeader,
  CircularProgress,
  Collapse,
  Divider,
  List,
} from '@mui/material';
import { AssignmentOutlined } from '@mui/icons-material';

import { useQuery } from '@apollo/client/react';

import { graphql } from '@/gql';
import { extractPaginationProps } from '@/utils/queries/queryExtracts';

import { Or2Card } from '@/components/Surface/Or2Card';
import { Or2ListItem } from '@/components/data-display/Or2ListItem';
import { Or2Link } from '@/components/navigation/Or2Link';
import { CurrentUserContext } from '@/context/CurrentUserContext';

import { NewsTasksCardListItem } from './news-tasks-card/NewsTasksCardListItem';

const NEWS_ASSIGNMENTS_QUERY = graphql(`
  query NewsAssignments($userId: ID!) {
    user(id: $userId) {
      _id
      assignmentsConnection(
        first: 5
        filterBy: { statuses: [PENDING, IN_PROGRESS] }
        orderBy: DUE_DATE
      ) {
        totalCount
        edges {
          node {
            ...newsTasksCardListItemFields
          }
        }
      }
    }
  }
`);

interface Props {
  className?: string;
}

export const NewsTasksCard = ({ className }: Props) => {
  const { currentUserId } = use(CurrentUserContext);

  const { data, loading } = useQuery(NEWS_ASSIGNMENTS_QUERY, {
    variables: { userId: currentUserId ?? '' },
    skip: !currentUserId,
    fetchPolicy: 'cache-and-network',
  });

  const { nodes: assignments, totalCount } = extractPaginationProps(
    data?.user?.assignmentsConnection,
  );

  const remainingCount = totalCount - assignments.length;

  const isNotAssignedTasks = !loading && assignments.length === 0;

  return (
    <Or2Card className={className}>
      <CardActionArea
        href={currentUserId ? `/user/${currentUserId}/tasks` : '#'}
      >
        <CardHeader
          sx={{
            '& .MuiCardHeader-action': {
              mt: 0,
              mr: 0,
              alignSelf: 'auto',
            },
          }}
          avatar={
            <Avatar
              sx={{
                backgroundColor: isNotAssignedTasks
                  ? 'error.main'
                  : 'primary.main',
              }}
            >
              <AssignmentOutlined />
            </Avatar>
          }
          action={
            loading ? (
              <CircularProgress
                sx={{ display: 'block' }}
                size={24}
                disableShrink
              />
            ) : null
          }
          title={isNotAssignedTasks ? 'No assigned tasks' : 'Assigned tasks'}
        />
      </CardActionArea>
      <Collapse in={assignments.length > 0} timeout="auto">
        <Divider />
        <List dense sx={{ py: 0.5 }}>
          {assignments.map((assignment) => (
            <NewsTasksCardListItem
              key={assignment._id}
              assignment={assignment}
              currentUserId={currentUserId}
            />
          ))}
          {remainingCount > 0 && currentUserId && (
            <Or2ListItem
              hoverHighlight={false}
              sx={{ py: 0.5, justifyContent: 'center' }}
            >
              <Or2Link
                href={`/user/${currentUserId}/tasks`}
                underline="hover"
                variant="body2"
                color="primary"
              >
                View {remainingCount} more
              </Or2Link>
            </Or2ListItem>
          )}
        </List>
      </Collapse>
    </Or2Card>
  );
};
